/* eslint-disable max-lines */
import { useQuery } from '@apollo/client/react';
import { GET_ARBEITSZEITEN_BY_DATE_AND_STANDORT_ID } from './graphql/queries';
import {
  Abteilung,
  Abwesenheit,
  Abwesenheitsgrund,
  Antrag,
  Arbeitsbereich,
  Arbeitszeit,
  Dienstgang,
  Mitarbeiter,
  Pause,
  Puenktlichkeit,
  Rechtshinweis,
  Zuschlag,
  Audit,
  ArbeitszeitAuditAenderung,
  ArbeitszeitAuditManager,
  Dienstplanuebernahme,
  BackgroundColor,
  ArbeitszeitenPausenart,
} from '../entities';
import { handleGraphQLErrors } from '../../../utils/errors/handleGraphQLErrors';
import {
  ArbeitszeitAuditAenderungsTyp,
  Arbeitszeiten,
  ArbeitszeitenByDatum,
  Markierung,
} from '../../../gql/graphql';
import { getDateOrNull } from '../../../utils/helperFunctions';
import { Termin, TerminWiederholung } from '../entities/Termin';
import { useToDos, UseToDosResult } from './useToDos';
import { Konflikt } from '../components/Table/ToDoTable/entities/Konflikt';
import { Arbeitszeit as ToDoArbeitszeit } from '../components/Table/ToDoTable/entities/Arbeitszeit';

function calculatePausendauer(arbeitszeit: Arbeitszeiten) {
  let pausendauer = 0;
  for (let i = 0; i < arbeitszeit.pausen.length; i++) {
    if (arbeitszeit.pausen[i].dauer !== null) {
      pausendauer += arbeitszeit.pausen[i].dauer;
    }
  }
  return pausendauer;
}

function calculatePuenktlichkeit(arbeitszeit: Arbeitszeiten) {
  let puenktlichkeit = new Puenktlichkeit();

  if (arbeitszeit.puenktlichkeit !== null) {
    puenktlichkeit = new Puenktlichkeit(
      arbeitszeit.puenktlichkeit.abweichungBeginn === null
        ? null
        : arbeitszeit.puenktlichkeit.abweichungBeginn,
      arbeitszeit.puenktlichkeit.abweichungEnde === null
        ? null
        : arbeitszeit.puenktlichkeit.abweichungEnde,
    );
  }
  return puenktlichkeit;
}

function mapDienstgaenge(arbeitszeit: Arbeitszeiten) {
  return arbeitszeit.dienstgaenge.map((dienstgang) => {
    return {
      id: dienstgang.id,
      beginn: getDateOrNull(dienstgang.beginn),
      ende: getDateOrNull(dienstgang.ende),
      dauer: dienstgang.dauer,
    };
  });
}

function mapPausen(arbeitszeit: Arbeitszeiten): Pause[] {
  return arbeitszeit.pausen.map((pause) => {
    return {
      id: pause.id,
      beginn: getDateOrNull(pause.beginn),
      ende: getDateOrNull(pause.ende),
      dauer: pause.dauer,
      art: ArbeitszeitenPausenart[pause.art],
      bezahlt: pause.bezahlt,
    };
  });
}

function mapRechtshinweise(arbeitszeit: Arbeitszeiten) {
  return arbeitszeit.rechtshinweise.map((rechtshinweis) => {
    return {
      name: rechtshinweis.name,
      art: rechtshinweis.art,
      backgroundcolor: BackgroundColor[rechtshinweis.backgroundcolor],
    };
  });
}

function mapAntraege(arbeitszeit: Arbeitszeiten): Antrag[] {
  return arbeitszeit.antraege?.map((antrag) => {
    return new Antrag(
      antrag.id,
      antrag.status,
      antrag.arbeitszeitId,
      antrag.offen,
      antrag.bemerkungAntragsteller,
      antrag.bemerkungVorgesetzter,
      getDateOrNull(antrag.datum),
      getDateOrNull(antrag.aenderungsdatum),
      getDateOrNull(antrag.erstellungsdatum),
      new Arbeitsbereich(
        antrag.arbeitsbereich.name,
        antrag.arbeitsbereich.id,
        antrag.arbeitsbereich.markierung as unknown as Markierung,
        [],
      ),
      new Mitarbeiter(
        antrag.mitarbeiter.vorname,
        antrag.mitarbeiter.nachname,
        antrag.mitarbeiter.id,
        false,
        antrag.mitarbeiter.bildUrl,
      ),
      {
        time:
          getDateOrNull(antrag.beginn) === null
            ? getDateOrNull(arbeitszeit.beginn)
            : getDateOrNull(antrag.beginn),
        isChangeRequested: antrag.beginn !== null,
      },
      {
        time: getDateOrNull(antrag.ende) ?? getDateOrNull(arbeitszeit.ende),
        isChangeRequested: antrag.ende !== null,
      },
    );
  });
}

function mapAudit(arbeitszeit: Arbeitszeiten): Audit[] {
  return arbeitszeit.audit.map((audit) => {
    if (audit.__typename === 'Audit') {
      return new Audit(
        audit.id,
        audit.typ,
        ArbeitszeitAuditAenderungsTyp[audit.aenderungsTyp],
        new Date(audit.aenderungsdatum),
        audit.geaendertVon,
        audit.geaendertVonEmail,
        new ArbeitszeitAuditManager(
          audit.manager.email,
          audit.manager.vorname,
          audit.manager.nachname,
        ),
        audit.aenderungen.map(
          (aenderung) =>
            new ArbeitszeitAuditAenderung(
              aenderung.attribut,
              aenderung.nachher,
              aenderung.vorher,
            ),
        ),
      );
    }
  });
}

function isArbeitszeit(
  arbeitszeit: Arbeitszeit | Antrag,
): arbeitszeit is Arbeitszeit {
  return arbeitszeit instanceof Arbeitszeit;
}

function isAntrag(arbeitszeit: Arbeitszeit | Antrag): arbeitszeit is Antrag {
  return arbeitszeit instanceof Antrag;
}

function isOffen(arbeitszeit: Arbeitszeit | Antrag): boolean {
  if (isArbeitszeit(arbeitszeit)) {
    return (
      (arbeitszeit.start === null || arbeitszeit.ende === null) &&
      arbeitszeit.abwesenheit === null
    );
  }

  if (isAntrag(arbeitszeit) && arbeitszeit.offen) {
    return arbeitszeit.status !== null;
  }
  return false;
}

export type ArbeitszeitenResult = {
  abgeschlosseneArbeitszeiten: Arbeitszeit[];
  todos: UseToDosResult;
  dienstplanuebernahmen: Dienstplanuebernahme[];
  offeneArbeitszeiten: (Arbeitszeit | Antrag)[];
  error?: string;
  loading: boolean;
};

// eslint-disable-next-line max-lines-per-function
export function useArbeitszeiten(
  datum: Date,
  standortId: string,
): ArbeitszeitenResult {
  const parsedDate = datum.toISOString().slice(0, 10);
  const { data, loading } = useQuery(
    GET_ARBEITSZEITEN_BY_DATE_AND_STANDORT_ID,
    {
      variables: {
        datum: parsedDate,
        standortId: standortId,
      },
      fetchPolicy: 'no-cache',
    },
  );

  const toDos = useToDos(parsedDate, standortId);

  if (loading || toDos.loading) {
    return {
      abgeschlosseneArbeitszeiten: [],
      todos: toDos,
      dienstplanuebernahmen: [],
      offeneArbeitszeiten: [],
      loading: true,
    };
  }

  const error = handleGraphQLErrors(data.arbeitszeiten);

  if (error) {
    return {
      error: error.message,
      abgeschlosseneArbeitszeiten: [],
      todos: {
        toDos: [],
        loading: false,
      },
      offeneArbeitszeiten: [],
      dienstplanuebernahmen: [],
      loading: false,
    };
  }

  const konfliktArbeitszeitIds = toDos.toDos
    .filter((a) => a._type === 'konflikt')
    .flatMap((a) => {
      const konflikt = a as Konflikt;
      const ids = [konflikt.rootArbeitszeit.id];
      if (konflikt.conflictCause instanceof ToDoArbeitszeit) {
        ids.push(konflikt.conflictCause.id);
      }
      return ids;
    });

  const zeiten = data.arbeitszeiten as ArbeitszeitenByDatum;

  const arbeitszeiten = zeiten.arbeitszeiten.map((arbeitszeit) => {
    return createArbeitszeit(arbeitszeit);
  });

  if (data.antraegeByStandortId.__typename !== 'ArbeitszeitAntraege') {
    throw new Error('Unexpected typename');
  }

  const abgeschlosseneArbeitszeiten = arbeitszeiten.filter((arbeitszeit) => {
    return (
      !isOffen(arbeitszeit) &&
      (!arbeitszeit.antraege ||
        arbeitszeit.antraege.every((antrag) => !antrag.offen))
    );
  });

  const dienstplanuebernahmen = data.dienstplanuebernahmen.map(
    (dienstplanuebernahme) =>
      new Dienstplanuebernahme(
        dienstplanuebernahme.id,
        dienstplanuebernahme.schichtId,
        getDateOrNull(dienstplanuebernahme.beginn),
        dienstplanuebernahme.pause,
        dienstplanuebernahme.dauer,
        new Mitarbeiter(
          dienstplanuebernahme.mitarbeiter.vorname,
          dienstplanuebernahme.mitarbeiter.nachname,
          dienstplanuebernahme.mitarbeiter.id,
          dienstplanuebernahme.mitarbeiter.extern,
          dienstplanuebernahme.mitarbeiter.bildUrl,
        ),
        new Arbeitsbereich(
          dienstplanuebernahme.arbeitsbereich.name,
          dienstplanuebernahme.arbeitsbereich.id,
          dienstplanuebernahme.arbeitsbereich
            .markierung as unknown as Markierung,
          [],
        ),
        getDateOrNull(dienstplanuebernahme.ende),
      ),
  );

  const antraegeResult = data.antraegeByStandortId.antraege;

  const antraege = antraegeResult.map((antrag) => {
    return new Antrag(
      antrag.id,
      antrag.status,
      antrag.arbeitszeitId,
      antrag.offen,
      antrag.bemerkungAntragsteller,
      antrag.bemerkungVorgesetzter,
      getDateOrNull(antrag.datum),
      getDateOrNull(antrag.aenderungsdatum),
      getDateOrNull(antrag.erstellungsdatum),
      new Arbeitsbereich(
        antrag.arbeitsbereich.name,
        antrag.arbeitsbereich.id,
        antrag.arbeitsbereich.markierung as unknown as Markierung,
        [],
      ),
      new Mitarbeiter(
        antrag.mitarbeiter.vorname,
        antrag.mitarbeiter.nachname,
        antrag.mitarbeiter.id,
        false,
        antrag.mitarbeiter.bildUrl,
      ),
      {
        time: getDateOrNull(antrag.beginn),
        isChangeRequested: antrag.beginn !== null,
      },
      {
        time: getDateOrNull(antrag.ende),
        isChangeRequested: antrag.ende !== null,
      },
    );
  });
  const offeneArbeitszeiten: (Arbeitszeit | Antrag)[] = [
    ...arbeitszeiten.filter((arbeitszeit) => {
      return (
        arbeitszeit.abwesenheit === null &&
        (arbeitszeit.start === null ||
          (arbeitszeit.ende === null && isOffen(arbeitszeit)) ||
          arbeitszeit?.antraege.some((antrag) => antrag.offen))
      );
    }),
    ...antraege.filter((antrag) => {
      return antrag.offen;
    }),
  ];

  return {
    todos: toDos,
    abgeschlosseneArbeitszeiten: abgeschlosseneArbeitszeiten.filter(
      (az) => konfliktArbeitszeitIds.includes(az.id) === false,
    ),
    dienstplanuebernahmen,
    offeneArbeitszeiten: offeneArbeitszeiten,
    loading: false,
  };
}

// eslint-disable-next-line max-lines-per-function
function createArbeitszeit(arbeitszeit: Arbeitszeiten) {
  const pausendauer = calculatePausendauer(arbeitszeit);
  const puenktlichkeit = calculatePuenktlichkeit(arbeitszeit);
  const dienstgaenge: Dienstgang[] = mapDienstgaenge(arbeitszeit);
  const bemerkung = arbeitszeit.bemerkung ? arbeitszeit.bemerkung : '';
  const pausen: Pause[] = mapPausen(arbeitszeit);
  const pauseneinstellung = arbeitszeit.pauseneinstellung;
  const zuschlaege: Zuschlag[] = [];
  const antraege: Antrag[] = mapAntraege(arbeitszeit);
  const rechtshinweise: Rechtshinweis[] = mapRechtshinweise(arbeitszeit);
  const audits: Audit[] = arbeitszeit.audit ? mapAudit(arbeitszeit) : [];
  let abwesenheit: Abwesenheit = null;

  if (arbeitszeit.abwesenheit !== null) {
    abwesenheit = new Abwesenheit(
      arbeitszeit.abwesenheit.id,
      Abwesenheitsgrund[arbeitszeit.abwesenheit.grund],
      arbeitszeit.abwesenheit.dauer,
    );
  }
  if (arbeitszeit.zuschlaege !== null) {
    const tmp = arbeitszeit.zuschlaege.map((zuschlag) => {
      if (zuschlag.betrag.__typename === 'arbeitszeiten_zuschlaege_betrag') {
        return new Zuschlag(
          zuschlag.dauer,
          zuschlag.name,
          zuschlag.prozent,
          zuschlag.betrag.betrag,
        );
      }
      return new Zuschlag(zuschlag.dauer, zuschlag.name, zuschlag.prozent);
    });
    zuschlaege.push(...tmp);
  }

  let termin: Termin | null = null;

  if (arbeitszeit.termin !== null) {
    termin = new Termin(
      arbeitszeit.termin.titel,
      TerminWiederholung[arbeitszeit.termin.wiederholung],
      arbeitszeit.termin.id,
      arbeitszeit.termin.bildUrl,
      arbeitszeit.termin.bildName,
      arbeitszeit.termin.beschreibung,
      null,
      null,
      arbeitszeit.termin.markierung,
    );
  }

  return new Arbeitszeit(
    arbeitszeit.id,
    new Mitarbeiter(
      arbeitszeit.mitarbeiter.vorname,
      arbeitszeit.mitarbeiter.nachname,
      arbeitszeit.mitarbeiter.id,
      arbeitszeit.mitarbeiter.extern,
      arbeitszeit.mitarbeiter.bildUrl,
    ),
    new Arbeitsbereich(
      arbeitszeit.arbeitsbereich.name,
      arbeitszeit.arbeitsbereich.id,
      arbeitszeit.arbeitsbereich.markierung,
      arbeitszeit.arbeitsbereich.abteilungen,
    ),
    new Abteilung(
      arbeitszeit.arbeitsbereich.name,
      arbeitszeit.arbeitsbereich.id,
      arbeitszeit.arbeitsbereich.markierung,
    ),
    arbeitszeit.dauer,
    pausendauer,
    puenktlichkeit,
    pausen,
    pauseneinstellung,
    dienstgaenge,
    [],
    rechtshinweise,
    '',
    getDateOrNull(arbeitszeit.beginn),
    getDateOrNull(arbeitszeit.ende),
    getDateOrNull(arbeitszeit.kommen),
    getDateOrNull(arbeitszeit.gehen),
    abwesenheit,
    bemerkung,
    zuschlaege,
    antraege,
    audits,
    termin,
    arbeitszeit.geaendertVon,
    getDateOrNull(arbeitszeit.aenderungsdatum),
    getDateOrNull(arbeitszeit.datum),
  );
}
